import React from 'react';
import PropTypes from 'prop-types';

import { backgroundGrey1, lightGrayBorder, veryLightGray } from 'lib/css/colors';
import { Badge, Col, ProgressBar, Row } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import dayjs from 'dayjs';

/**
 * Individual row component for displaying a single concurrent sync
 * Shows detailed information about a running GDSN sync process
 */
function ConcurrentSyncRow({ run }) {
  const manufacturer = run.run_metadata?.manufacturer;
  const glns = run.run_metadata?.manufacturer_glns;
  const progress = run.run_metadata?.current_row || 0;
  const total = run.run_metadata?.total_rows || 0;
  const processedCount = run.run_metadata?.processed_count || 0;
  const errorCount = run.run_metadata?.error_count || 0;

  const progressPercentage = total > 0 ? Math.round((progress / total) * 100) : 0;
  const duration = dayjs().diff(dayjs(run.date_created), 'minute');

  return (
    <Row className={css(styles.syncRow)}>
      <Col md={3}>
        <div>
          <strong>Run #{run.id}</strong>
          <br />
          <small className="text-muted">Started: {dayjs(run.date_created).format('HH:mm')}</small>
          <br />
          <small className="text-muted">Duration: {duration}m</small>
        </div>
      </Col>

      <Col md={3}>
        {manufacturer && (
          <div>
            <Badge variant="primary" className="mb-1">
              {manufacturer}
            </Badge>
            {glns && glns.length > 0 && (
              <div className={css(styles.glnContainer)}>
                {glns.slice(0, 2).map(gln => (
                  <Badge key={gln} variant="outline-secondary" className={css(styles.glnBadge)}>
                    {gln}
                  </Badge>
                ))}
                {glns.length > 2 && (
                  <Badge variant="outline-secondary" className={css(styles.glnBadge)}>
                    +{glns.length - 2} more
                  </Badge>
                )}
              </div>
            )}
          </div>
        )}
      </Col>

      <Col md={4}>
        {total > 0 ? (
          <div>
            <div className="d-flex justify-content-between mb-1">
              <small>Progress</small>
              <small>
                {progress}/{total} messages
              </small>
            </div>
            <ProgressBar
              now={progressPercentage}
              size="sm"
              variant={progressPercentage > 80 ? 'success' : 'primary'}
            />
            <div className="d-flex justify-content-between mt-1">
              <small className="text-success">✓ {processedCount}</small>
              {errorCount > 0 && <small className="text-danger">✗ {errorCount}</small>}
            </div>
          </div>
        ) : (
          <div className="text-muted">
            <small>Initializing...</small>
          </div>
        )}
      </Col>

      <Col md={2} className="text-right">
        <Badge variant="info" className={css(styles.statusBadge)}>
          In Progress
        </Badge>
      </Col>
    </Row>
  );
}

const styles = StyleSheet.create({
  syncRow: {
    padding: '0.75rem',
    marginBottom: '0.5rem',
    border: `1px solid ${lightGrayBorder}`,
    borderRadius: '0.375rem',
    backgroundColor: backgroundGrey1,
    ':hover': {
      backgroundColor: veryLightGray,
    },
    ':last-child': {
      marginBottom: 0,
    },
  },
  glnContainer: {
    marginTop: '0.25rem',
  },
  glnBadge: {
    marginRight: '0.25rem',
    marginBottom: '0.25rem',
    fontSize: '0.7rem',
  },
  statusBadge: {
    fontSize: '0.75rem',
  },
});

// PropTypes validation
ConcurrentSyncRow.propTypes = {
  run: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    date_created: PropTypes.string.isRequired,
    run_metadata: PropTypes.shape({
      manufacturer: PropTypes.string,
      manufacturer_glns: PropTypes.arrayOf(PropTypes.string),
      current_row: PropTypes.number,
      total_rows: PropTypes.number,
      processed_count: PropTypes.number,
      error_count: PropTypes.number,
    }),
  }).isRequired,
};

export default ConcurrentSyncRow;
